import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Card, Button, Alert, Paper, Select, MenuItem,
  FormControl, InputLabel, Chip, CircularProgress, Divider, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Collapse, alpha, useTheme
} from '@mui/material';
import axios from '../utils/axios';

/* ── Inline SVG Icons ─────────────────────────────────────────────── */
const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 10a7 7 0 0112.9-3.7M17 10a7 7 0 01-12.9 3.7" strokeLinecap="round" />
    <path d="M16 3v4h-4M4 17v-4h4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="9" cy="9" r="6" /><path d="M13.5 13.5L17 17" strokeLinecap="round" />
  </svg>
);
const ChevronIcon = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
    <path d="M7 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 3v10M6 9l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 15v2h14v-2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ServerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="14" height="5" rx="1.5" /><rect x="3" y="12" width="14" height="5" rx="1.5" />
    <circle cx="6" cy="5.5" r="0.75" fill="currentColor" /><circle cx="6" cy="14.5" r="0.75" fill="currentColor" />
  </svg>
);
const DatabaseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <ellipse cx="10" cy="5" rx="7" ry="3" /><path d="M3 5v10c0 1.66 3.13 3 7 3s7-1.34 7-3V5" />
    <path d="M3 10c0 1.66 3.13 3 7 3s7-1.34 7-3" />
  </svg>
);
const BucketIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 4h12l-1.5 13H5.5L4 4z" strokeLinejoin="round" /><path d="M3 4h14" strokeLinecap="round" />
  </svg>
);
const LambdaIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 17L8 3h2l3 8.5L17 3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const DiskIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="10" cy="10" r="7" /><circle cx="10" cy="10" r="2" />
  </svg>
);
const LoadBalancerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="10" cy="4" r="2" /><circle cx="5" cy="16" r="2" /><circle cx="15" cy="16" r="2" />
    <path d="M10 6v4M8 12l-3 2M12 12l3 2" strokeLinecap="round" />
  </svg>
);
const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 2L3 5v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V5l-7-3z" strokeLinejoin="round" />
  </svg>
);
const IpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="10" cy="10" r="7" /><path d="M3 10h14M10 3c2 2.5 3 5 3 7s-1 4.5-3 7c-2-2.5-3-5-3-7s1-4.5 3-7z" />
  </svg>
);
const NatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="7" width="14" height="6" rx="1.5" /><path d="M7 7V5M13 7V5M7 13v2M13 13v2" strokeLinecap="round" />
  </svg>
);
const SnapshotIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="14" height="14" rx="2" /><circle cx="10" cy="10" r="3" /><path d="M14 3l-4 4" strokeLinecap="round" />
  </svg>
);

const SERVICE_CONFIG = {
  ec2: { label: 'EC2 Instances', icon: ServerIcon, color: '#2563EB' },
  rds: { label: 'RDS Databases', icon: DatabaseIcon, color: '#8B5CF6' },
  s3: { label: 'S3 Buckets', icon: BucketIcon, color: '#10B981' },
  lambda: { label: 'Lambda Functions', icon: LambdaIcon, color: '#F59E0B' },
  ebs: { label: 'EBS Volumes', icon: DiskIcon, color: '#EC4899' },
  elb: { label: 'Load Balancers', icon: LoadBalancerIcon, color: '#06B6D4' },
  securityGroups: { label: 'Security Groups', icon: ShieldIcon, color: '#EF4444' },
  eips: { label: 'Elastic IPs', icon: IpIcon, color: '#84CC16' },
  natGateways: { label: 'NAT Gateways', icon: NatIcon, color: '#F97316' },
  snapshots: { label: 'Snapshots', icon: SnapshotIcon, color: '#6366F1' },
};

const TH = { fontWeight: 600, fontSize: '0.75rem' };
const TD = { fontSize: '0.75rem' };
const MONO = { fontSize: '0.75rem', fontFamily: 'monospace' };
const MONO_TRUNC = { ...MONO, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

function stateChip(state) {
  if (!state) return null;
  const s = state.toLowerCase();
  const color = ['running', 'available', 'active', 'in-use'].includes(s) ? 'success'
    : ['stopped', 'deleted', 'terminated'].includes(s) ? 'error'
    : ['pending', 'creating', 'modifying'].includes(s) ? 'warning' : 'default';
  return <Chip label={state} size="small" color={color} variant="outlined" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }} />;
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function accessChip(level) {
  if (!level) return '-';
  const isPrivate = level === 'Private';
  return <Chip label={level} size="small" color={isPrivate ? 'success' : 'warning'} variant="outlined" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }} />;
}

/* ── Excel Export Utility ──────────────────────────────────────────── */
function escapeCSV(val) {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

function generateCSVSheet(items, columns) {
  const header = columns.map(c => escapeCSV(c.label)).join(',');
  const rows = items.map(item =>
    columns.map(c => escapeCSV(c.getValue ? c.getValue(item) : (item[c.key] ?? ''))).join(',')
  );
  return header + '\n' + rows.join('\n');
}

function downloadInventoryExcel(inventory, accountName) {
  const sheets = {
    ec2: { label: 'EC2 Instances', columns: [
      { key: 'resourceId', label: 'Instance ID' }, { key: 'name', label: 'Name' },
      { key: 'state', label: 'State' }, { key: 'type', label: 'Type' },
      { key: 'az', label: 'Availability Zone' }, { key: 'privateIp', label: 'Private IP' },
      { key: 'publicIp', label: 'Public IP' }, { key: 'vpcId', label: 'VPC ID' },
      { key: 'subnetId', label: 'Subnet ID' }, { key: 'platform', label: 'Platform' },
      { key: 'amiId', label: 'AMI ID' }, { key: 'keyName', label: 'Key Pair' },
      { key: 'architecture', label: 'Architecture' },
      { key: 'launchTime', label: 'Launch Time', getValue: i => formatDate(i.launchTime) },
    ]},
    rds: { label: 'RDS Databases', columns: [
      { key: 'resourceId', label: 'DB Identifier' },
      { key: 'engine', label: 'Engine', getValue: i => `${i.engine} ${i.engineVersion || ''}`.trim() },
      { key: 'status', label: 'Status' }, { key: 'class', label: 'Class' },
      { key: 'allocatedStorage', label: 'Storage (GB)' }, { key: 'storageType', label: 'Storage Type' },
      { key: 'multiAZ', label: 'Multi-AZ', getValue: i => i.multiAZ ? 'Yes' : 'No' },
      { key: 'storageEncrypted', label: 'Encrypted', getValue: i => i.storageEncrypted ? 'Yes' : 'No' },
      { key: 'publiclyAccessible', label: 'Public', getValue: i => i.publiclyAccessible ? 'Yes' : 'No' },
      { key: 'port', label: 'Port' }, { key: 'backupRetention', label: 'Backup Retention (days)' },
      { key: 'endpoint', label: 'Endpoint' }, { key: 'az', label: 'AZ' },
      { key: 'createTime', label: 'Created', getValue: i => formatDate(i.createTime) },
    ]},
    s3: { label: 'S3 Buckets', columns: [
      { key: 'name', label: 'Bucket Name' }, { key: 'region', label: 'Region' },
      { key: 'accessLevel', label: 'Access Level' }, { key: 'versioning', label: 'Versioning' },
      { key: 'encryption', label: 'Encryption' },
      { key: 'createTime', label: 'Created', getValue: i => formatDate(i.createTime) },
    ]},
    lambda: { label: 'Lambda Functions', columns: [
      { key: 'name', label: 'Function Name' }, { key: 'runtime', label: 'Runtime' },
      { key: 'memorySize', label: 'Memory (MB)' }, { key: 'timeout', label: 'Timeout (s)' },
      { key: 'codeSize', label: 'Code Size (KB)', getValue: i => (i.codeSize / 1024).toFixed(1) },
      { key: 'architecture', label: 'Architecture' }, { key: 'layers', label: 'Layers' },
      { key: 'handler', label: 'Handler' },
      { key: 'lastModified', label: 'Last Modified', getValue: i => formatDate(i.lastModified) },
    ]},
    ebs: { label: 'EBS Volumes', columns: [
      { key: 'resourceId', label: 'Volume ID' }, { key: 'name', label: 'Name' },
      { key: 'state', label: 'State' }, { key: 'type', label: 'Type' },
      { key: 'size', label: 'Size (GB)' }, { key: 'iops', label: 'IOPS' },
      { key: 'throughput', label: 'Throughput (MiB/s)' },
      { key: 'encrypted', label: 'Encrypted', getValue: i => i.encrypted ? 'Yes' : 'No' },
      { key: 'attachedTo', label: 'Attached To' }, { key: 'az', label: 'AZ' },
      { key: 'createTime', label: 'Created', getValue: i => formatDate(i.createTime) },
    ]},
    elb: { label: 'Load Balancers', columns: [
      { key: 'name', label: 'Name' }, { key: 'type', label: 'Type' },
      { key: 'state', label: 'State' }, { key: 'scheme', label: 'Scheme' },
      { key: 'dnsName', label: 'DNS Name' }, { key: 'vpcId', label: 'VPC ID' },
      { key: 'azs', label: 'Availability Zones' },
      { key: 'createTime', label: 'Created', getValue: i => formatDate(i.createTime) },
    ]},
    securityGroups: { label: 'Security Groups', columns: [
      { key: 'resourceId', label: 'Group ID' }, { key: 'name', label: 'Name' },
      { key: 'description', label: 'Description' }, { key: 'vpcId', label: 'VPC' },
      { key: 'inboundRules', label: 'Inbound Rules' }, { key: 'outboundRules', label: 'Outbound Rules' },
    ]},
    eips: { label: 'Elastic IPs', columns: [
      { key: 'resourceId', label: 'Allocation ID' }, { key: 'publicIp', label: 'Public IP' },
      { key: 'associatedInstance', label: 'Associated Instance' }, { key: 'domain', label: 'Domain' },
    ]},
    natGateways: { label: 'NAT Gateways', columns: [
      { key: 'resourceId', label: 'NAT Gateway ID' }, { key: 'name', label: 'Name' },
      { key: 'state', label: 'State' }, { key: 'subnetId', label: 'Subnet' },
      { key: 'vpcId', label: 'VPC' },
      { key: 'createTime', label: 'Created', getValue: i => formatDate(i.createTime) },
    ]},
    snapshots: { label: 'Snapshots', columns: [
      { key: 'resourceId', label: 'Snapshot ID' }, { key: 'name', label: 'Name' },
      { key: 'state', label: 'State' }, { key: 'volumeId', label: 'Volume ID' },
      { key: 'size', label: 'Size (GB)' },
      { key: 'encrypted', label: 'Encrypted', getValue: i => i.encrypted ? 'Yes' : 'No' },
      { key: 'startTime', label: 'Created', getValue: i => formatDate(i.startTime) },
    ]},
  };

  // Build a multi-sheet CSV (one file per sheet, zipped would need a library — use combined CSV with sheet separators)
  // For simplicity and no-library approach: generate one CSV with all sheets separated by headers
  let combined = '';
  Object.entries(sheets).forEach(([key, cfg]) => {
    const items = inventory[key] || [];
    if (items.length === 0) return;
    combined += `\n${cfg.label} (${items.length})\n`;
    combined += generateCSVSheet(items, cfg.columns) + '\n';
  });

  const blob = new Blob([combined], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `AWS_Inventory_${accountName || 'export'}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/* ── Service-specific table renderers ──────────────────────────────── */
function EC2Table({ items }) {
  return (
    <TableContainer><Table size="small">
      <TableHead><TableRow>
        <TableCell sx={TH}>Instance ID</TableCell>
        <TableCell sx={TH}>Name</TableCell>
        <TableCell sx={TH}>State</TableCell>
        <TableCell sx={TH}>Type</TableCell>
        <TableCell sx={TH}>AZ</TableCell>
        <TableCell sx={TH}>Private IP</TableCell>
        <TableCell sx={TH}>Public IP</TableCell>
        <TableCell sx={TH}>VPC</TableCell>
        <TableCell sx={TH}>Platform</TableCell>
        <TableCell sx={TH}>AMI ID</TableCell>
        <TableCell sx={TH}>Key Pair</TableCell>
        <TableCell sx={TH}>Arch</TableCell>
        <TableCell sx={TH}>Launched</TableCell>
      </TableRow></TableHead>
      <TableBody>{items.map(i => (
        <TableRow key={i.resourceId} hover>
          <TableCell sx={MONO}>{i.resourceId}</TableCell>
          <TableCell sx={TD}>{i.name || '-'}</TableCell>
          <TableCell>{stateChip(i.state)}</TableCell>
          <TableCell sx={TD}>{i.type}</TableCell>
          <TableCell sx={TD}>{i.az}</TableCell>
          <TableCell sx={MONO}>{i.privateIp || '-'}</TableCell>
          <TableCell sx={MONO}>{i.publicIp || '-'}</TableCell>
          <TableCell sx={MONO_TRUNC}>{i.vpcId || '-'}</TableCell>
          <TableCell sx={TD}>{i.platform}</TableCell>
          <TableCell sx={MONO_TRUNC}>{i.amiId || '-'}</TableCell>
          <TableCell sx={TD}>{i.keyName || '-'}</TableCell>
          <TableCell sx={TD}>{i.architecture || '-'}</TableCell>
          <TableCell sx={TD}>{formatDate(i.launchTime)}</TableCell>
        </TableRow>
      ))}</TableBody>
    </Table></TableContainer>
  );
}

function RDSTable({ items }) {
  return (
    <TableContainer><Table size="small">
      <TableHead><TableRow>
        <TableCell sx={TH}>DB Identifier</TableCell>
        <TableCell sx={TH}>Engine</TableCell>
        <TableCell sx={TH}>Status</TableCell>
        <TableCell sx={TH}>Class</TableCell>
        <TableCell sx={TH}>Storage</TableCell>
        <TableCell sx={TH}>Type</TableCell>
        <TableCell sx={TH}>Multi-AZ</TableCell>
        <TableCell sx={TH}>Encrypted</TableCell>
        <TableCell sx={TH}>Public</TableCell>
        <TableCell sx={TH}>Port</TableCell>
        <TableCell sx={TH}>Backup (days)</TableCell>
        <TableCell sx={TH}>Endpoint</TableCell>
        <TableCell sx={TH}>Created</TableCell>
      </TableRow></TableHead>
      <TableBody>{items.map(i => (
        <TableRow key={i.resourceId} hover>
          <TableCell sx={MONO}>{i.resourceId}</TableCell>
          <TableCell sx={TD}>{i.engine} {i.engineVersion}</TableCell>
          <TableCell>{stateChip(i.status)}</TableCell>
          <TableCell sx={TD}>{i.class}</TableCell>
          <TableCell sx={TD}>{i.allocatedStorage} GB</TableCell>
          <TableCell sx={TD}>{i.storageType || '-'}</TableCell>
          <TableCell sx={TD}>{i.multiAZ ? 'Yes' : 'No'}</TableCell>
          <TableCell sx={TD}>{i.storageEncrypted ? 'Yes' : 'No'}</TableCell>
          <TableCell sx={TD}>{i.publiclyAccessible ? 'Yes' : 'No'}</TableCell>
          <TableCell sx={TD}>{i.port || '-'}</TableCell>
          <TableCell sx={TD}>{i.backupRetention ?? '-'}</TableCell>
          <TableCell sx={{ ...MONO, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.endpoint || '-'}</TableCell>
          <TableCell sx={TD}>{formatDate(i.createTime)}</TableCell>
        </TableRow>
      ))}</TableBody>
    </Table></TableContainer>
  );
}

function S3Table({ items }) {
  return (
    <TableContainer><Table size="small">
      <TableHead><TableRow>
        <TableCell sx={TH}>Bucket Name</TableCell>
        <TableCell sx={TH}>Region</TableCell>
        <TableCell sx={TH}>Access Level</TableCell>
        <TableCell sx={TH}>Versioning</TableCell>
        <TableCell sx={TH}>Encryption</TableCell>
        <TableCell sx={TH}>Created</TableCell>
      </TableRow></TableHead>
      <TableBody>{items.map(i => (
        <TableRow key={i.resourceId} hover>
          <TableCell sx={MONO}>{i.name}</TableCell>
          <TableCell sx={TD}>{i.region || '-'}</TableCell>
          <TableCell>{accessChip(i.accessLevel)}</TableCell>
          <TableCell sx={TD}>
            <Chip label={i.versioning || 'Disabled'} size="small" variant="outlined"
              color={i.versioning === 'Enabled' ? 'success' : 'default'}
              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }} />
          </TableCell>
          <TableCell sx={TD}>
            <Chip label={i.encryption || 'None'} size="small" variant="outlined"
              color={i.encryption && i.encryption !== 'None' ? 'info' : 'default'}
              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }} />
          </TableCell>
          <TableCell sx={TD}>{formatDate(i.createTime)}</TableCell>
        </TableRow>
      ))}</TableBody>
    </Table></TableContainer>
  );
}

function LambdaTable({ items }) {
  return (
    <TableContainer><Table size="small">
      <TableHead><TableRow>
        <TableCell sx={TH}>Function Name</TableCell>
        <TableCell sx={TH}>Runtime</TableCell>
        <TableCell sx={TH}>Memory (MB)</TableCell>
        <TableCell sx={TH}>Timeout (s)</TableCell>
        <TableCell sx={TH}>Code Size</TableCell>
        <TableCell sx={TH}>Arch</TableCell>
        <TableCell sx={TH}>Layers</TableCell>
        <TableCell sx={TH}>Handler</TableCell>
        <TableCell sx={TH}>Last Modified</TableCell>
      </TableRow></TableHead>
      <TableBody>{items.map(i => (
        <TableRow key={i.resourceId} hover>
          <TableCell sx={MONO}>{i.name}</TableCell>
          <TableCell sx={TD}>{i.runtime || '-'}</TableCell>
          <TableCell sx={TD}>{i.memorySize}</TableCell>
          <TableCell sx={TD}>{i.timeout}</TableCell>
          <TableCell sx={TD}>{(i.codeSize / 1024).toFixed(1)} KB</TableCell>
          <TableCell sx={TD}>{i.architecture || '-'}</TableCell>
          <TableCell sx={TD}>{i.layers ?? 0}</TableCell>
          <TableCell sx={MONO_TRUNC}>{i.handler || '-'}</TableCell>
          <TableCell sx={TD}>{formatDate(i.lastModified)}</TableCell>
        </TableRow>
      ))}</TableBody>
    </Table></TableContainer>
  );
}

function EBSTable({ items }) {
  return (
    <TableContainer><Table size="small">
      <TableHead><TableRow>
        <TableCell sx={TH}>Volume ID</TableCell>
        <TableCell sx={TH}>Name</TableCell>
        <TableCell sx={TH}>State</TableCell>
        <TableCell sx={TH}>Type</TableCell>
        <TableCell sx={TH}>Size (GB)</TableCell>
        <TableCell sx={TH}>IOPS</TableCell>
        <TableCell sx={TH}>Throughput</TableCell>
        <TableCell sx={TH}>Encrypted</TableCell>
        <TableCell sx={TH}>Attached To</TableCell>
        <TableCell sx={TH}>AZ</TableCell>
        <TableCell sx={TH}>Created</TableCell>
      </TableRow></TableHead>
      <TableBody>{items.map(i => (
        <TableRow key={i.resourceId} hover>
          <TableCell sx={MONO}>{i.resourceId}</TableCell>
          <TableCell sx={TD}>{i.name || '-'}</TableCell>
          <TableCell>{stateChip(i.state)}</TableCell>
          <TableCell sx={TD}>{i.type}</TableCell>
          <TableCell sx={TD}>{i.size}</TableCell>
          <TableCell sx={TD}>{i.iops || '-'}</TableCell>
          <TableCell sx={TD}>{i.throughput ? `${i.throughput} MiB/s` : '-'}</TableCell>
          <TableCell sx={TD}>{i.encrypted ? 'Yes' : 'No'}</TableCell>
          <TableCell sx={MONO}>{i.attachedTo || 'Unattached'}</TableCell>
          <TableCell sx={TD}>{i.az || '-'}</TableCell>
          <TableCell sx={TD}>{formatDate(i.createTime)}</TableCell>
        </TableRow>
      ))}</TableBody>
    </Table></TableContainer>
  );
}

function ELBTable({ items }) {
  return (
    <TableContainer><Table size="small">
      <TableHead><TableRow>
        <TableCell sx={TH}>Name</TableCell>
        <TableCell sx={TH}>Type</TableCell>
        <TableCell sx={TH}>State</TableCell>
        <TableCell sx={TH}>Scheme</TableCell>
        <TableCell sx={TH}>DNS Name</TableCell>
        <TableCell sx={TH}>VPC</TableCell>
        <TableCell sx={TH}>AZs</TableCell>
        <TableCell sx={TH}>Created</TableCell>
      </TableRow></TableHead>
      <TableBody>{items.map(i => (
        <TableRow key={i.resourceId || i.name} hover>
          <TableCell sx={TD}>{i.name}</TableCell>
          <TableCell sx={TD}><Chip label={i.type || 'classic'} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} /></TableCell>
          <TableCell>{stateChip(i.state)}</TableCell>
          <TableCell sx={TD}>{i.scheme || '-'}</TableCell>
          <TableCell sx={MONO_TRUNC}>{i.dnsName || '-'}</TableCell>
          <TableCell sx={MONO_TRUNC}>{i.vpcId || '-'}</TableCell>
          <TableCell sx={TD}>{i.azs || '-'}</TableCell>
          <TableCell sx={TD}>{formatDate(i.createTime)}</TableCell>
        </TableRow>
      ))}</TableBody>
    </Table></TableContainer>
  );
}

function GenericTable({ items, columns }) {
  return (
    <TableContainer><Table size="small">
      <TableHead><TableRow>
        {columns.map(c => <TableCell key={c.key} sx={TH}>{c.label}</TableCell>)}
      </TableRow></TableHead>
      <TableBody>{items.map((item, idx) => (
        <TableRow key={idx} hover>
          {columns.map(c => (
            <TableCell key={c.key} sx={c.mono ? MONO : TD}>
              {c.render ? c.render(item[c.key], item) : (item[c.key] ?? '-')}
            </TableCell>
          ))}
        </TableRow>
      ))}</TableBody>
    </Table></TableContainer>
  );
}

const GENERIC_COLUMNS = {
  securityGroups: [
    { key: 'resourceId', label: 'Group ID', mono: true },
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'vpcId', label: 'VPC', mono: true },
    { key: 'inboundRules', label: 'Inbound Rules' },
    { key: 'outboundRules', label: 'Outbound Rules' },
  ],
  eips: [
    { key: 'resourceId', label: 'Allocation ID', mono: true },
    { key: 'publicIp', label: 'Public IP', mono: true },
    { key: 'associatedInstance', label: 'Associated Instance', mono: true },
    { key: 'domain', label: 'Domain' },
  ],
  natGateways: [
    { key: 'resourceId', label: 'NAT Gateway ID', mono: true },
    { key: 'name', label: 'Name' },
    { key: 'state', label: 'State', render: (v) => stateChip(v) },
    { key: 'subnetId', label: 'Subnet', mono: true },
    { key: 'vpcId', label: 'VPC', mono: true },
    { key: 'createTime', label: 'Created', render: (v) => formatDate(v) },
  ],
  snapshots: [
    { key: 'resourceId', label: 'Snapshot ID', mono: true },
    { key: 'name', label: 'Name' },
    { key: 'state', label: 'State', render: (v) => stateChip(v) },
    { key: 'volumeId', label: 'Volume ID', mono: true },
    { key: 'size', label: 'Size (GB)' },
    { key: 'encrypted', label: 'Encrypted', render: (v) => v ? 'Yes' : 'No' },
    { key: 'startTime', label: 'Created', render: (v) => formatDate(v) },
  ],
};

function renderServiceTable(serviceKey, items) {
  switch (serviceKey) {
    case 'ec2': return <EC2Table items={items} />;
    case 'rds': return <RDSTable items={items} />;
    case 's3': return <S3Table items={items} />;
    case 'lambda': return <LambdaTable items={items} />;
    case 'ebs': return <EBSTable items={items} />;
    case 'elb': return <ELBTable items={items} />;
    default:
      if (GENERIC_COLUMNS[serviceKey]) return <GenericTable items={items} columns={GENERIC_COLUMNS[serviceKey]} />;
      return <Typography variant="body2" color="text.secondary">No table renderer for this service.</Typography>;
  }
}

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════════ */
function Inventory() {
  const theme = useTheme();
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [filterService, setFilterService] = useState('all');

  useEffect(() => { loadAccounts(); }, []);
  useEffect(() => { if (selectedAccount) fetchInventory(); }, [selectedAccount]);

  const loadAccounts = async () => {
    try {
      const res = await axios.get('/api/aws/accounts');
      setAwsAccounts(res.data.accounts || []);
      if (res.data.accounts?.length > 0) setSelectedAccount(res.data.accounts[0].id);
    } catch (err) { console.error('Failed to load accounts:', err); }
  };

  const fetchInventory = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/cost/inventory/${selectedAccount}`);
      setData(res.data);
      const autoExpand = {};
      Object.entries(res.data.inventory || {}).forEach(([key, arr]) => {
        if (arr.length > 0) autoExpand[key] = true;
      });
      setExpanded(autoExpand);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); }
  };

  const toggleSection = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const filteredInventory = useMemo(() => {
    if (!data?.inventory) return {};
    const result = {};
    Object.entries(data.inventory).forEach(([key, items]) => {
      if (filterService !== 'all' && filterService !== key) return;
      if (items.length === 0) return;
      if (!search.trim()) { result[key] = items; return; }
      const q = search.toLowerCase();
      const filtered = items.filter(item =>
        Object.values(item).some(v =>
          typeof v === 'string' && v.toLowerCase().includes(q)
        )
      );
      if (filtered.length > 0) result[key] = filtered;
    });
    return result;
  }, [data, search, filterService]);

  const totalFiltered = Object.values(filteredInventory).reduce((sum, arr) => sum + arr.length, 0);

  const handleExport = useCallback(() => {
    if (!data?.inventory) return;
    const acct = awsAccounts.find(a => a.id === selectedAccount);
    downloadInventoryExcel(data.inventory, acct?.account_name || 'export');
  }, [data, awsAccounts, selectedAccount]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.3rem' }}>Resource Inventory</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            Complete inventory of all AWS resources across services
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>AWS Account</InputLabel>
            <Select value={selectedAccount} onChange={e => setSelectedAccount(e.target.value)} label="AWS Account" sx={{ fontSize: '0.8rem' }}>
              {awsAccounts.map(acc => <MenuItem key={acc.id} value={acc.id}>{acc.account_name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchInventory} disabled={loading || !selectedAccount}
            sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.8rem' }}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
          {data && (
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport}
              sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.8rem', bgcolor: '#10B981', '&:hover': { bgcolor: '#059669' } }}>
              Export CSV
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {data?.errors?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          Some services had permission issues: {data.errors.map(e => e.service).join(', ')}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Scanning AWS resources...</Typography>
          </Box>
        </Box>
      )}

      {!loading && data && (
        <>
          {/* Summary cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2.5 }}>
            {Object.entries(SERVICE_CONFIG).map(([key, cfg]) => {
              const count = data.inventory?.[key]?.length || 0;
              if (count === 0) return null;
              return (
                <Paper key={key} elevation={0} onClick={() => { setFilterService(filterService === key ? 'all' : key); }}
                  sx={{
                    p: 1.5, borderRadius: 2, cursor: 'pointer', transition: 'all 0.15s',
                    border: `1px solid ${filterService === key ? cfg.color : theme.palette.divider}`,
                    bgcolor: filterService === key ? alpha(cfg.color, 0.04) : 'transparent',
                    '&:hover': { borderColor: cfg.color, bgcolor: alpha(cfg.color, 0.03) }
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{ color: cfg.color, display: 'flex' }}>{cfg.icon && React.createElement(cfg.icon)}</Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>{cfg.label}</Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: cfg.color }}>{count}</Typography>
                </Paper>
              );
            })}
          </Box>

          {/* Search + filter bar */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search resources..." value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <Box sx={{ mr: 1, display: 'flex', color: 'text.disabled' }}><SearchIcon /></Box> }}
              sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }}
            />
            <Chip label={`${totalFiltered} resources`} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
            {filterService !== 'all' && (
              <Chip label={`Filtered: ${SERVICE_CONFIG[filterService]?.label}`} size="small" onDelete={() => setFilterService('all')}
                sx={{ fontWeight: 500, fontSize: '0.72rem' }} />
            )}
            {data.duration && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                Scanned in {(data.duration / 1000).toFixed(1)}s
              </Typography>
            )}
          </Box>

          {/* Service sections */}
          {Object.entries(filteredInventory).map(([serviceKey, items]) => {
            const cfg = SERVICE_CONFIG[serviceKey];
            if (!cfg) return null;
            const isOpen = expanded[serviceKey];
            return (
              <Card key={serviceKey} elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                <Box onClick={() => toggleSection(serviceKey)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, cursor: 'pointer',
                    bgcolor: isOpen ? alpha(cfg.color, 0.03) : 'transparent',
                    '&:hover': { bgcolor: alpha(cfg.color, 0.05) }, transition: 'background 0.15s'
                  }}>
                  <Box sx={{ color: cfg.color, display: 'flex' }}>{cfg.icon && React.createElement(cfg.icon)}</Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem', flex: 1 }}>{cfg.label}</Typography>
                  <Chip label={items.length} size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(cfg.color, 0.1), color: cfg.color }} />
                  <Box sx={{ color: 'text.secondary', display: 'flex' }}><ChevronIcon open={isOpen} /></Box>
                </Box>
                <Collapse in={isOpen}>
                  <Divider />
                  <Box sx={{ overflowX: 'auto' }}>
                    {renderServiceTable(serviceKey, items)}
                  </Box>
                </Collapse>
              </Card>
            );
          })}

          {Object.keys(filteredInventory).length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body2" color="text.secondary">No resources found matching your search.</Typography>
            </Box>
          )}
        </>
      )}

      {!loading && !data && !error && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body2" color="text.secondary">Select an AWS account to view resource inventory.</Typography>
        </Box>
      )}
    </Box>
  );
}

export default Inventory;
